import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Book, ChevronDown, Layers, CreditCard, BookOpen, Maximize2, Minimize2 } from "lucide-react";
import { JUZ_DATA } from "@/data/quranMeta";
import OfflineDownloadButton from "@/components/OfflineDownloadButton";
import ThemeToggle from "@/components/ThemeToggle";
import ReciterPicker from "@/components/ReciterPicker";
import { clearTutorialFlag } from "@/components/ReaderTutorial";

/**
 * Sticky top toolbar for the Reader page.
 * Contains: Back button, clickable Surah name (opens SurahSelector),
 * Juz picker, view-mode toggle buttons, help (?), and offline download.
 */
export default function ReaderHeader({
  surah,
  surahNumber,
  reciter,
  onReciterChange,
  reviewMode,
  stackMode,
  stackLevel,
  viewMode,
  currentAyahIndex,
  displayAyahs,
  totalAyahs,
  selectedJuz,
  changeViewMode,
  onOpenSurahSelector,
  onShowTutorial,
  fullscreenMode,
  onToggleFullscreen,
}) {
  const navigate = useNavigate();

  const handleJuzChange = (juzNum) => {
    const juz = JUZ_DATA.find((j) => j.juz === parseInt(juzNum));
    if (juz) navigate(`/reader/${juz.start.surah}`);
  };

  return (
    <header
      data-testid="reader-header"
      className="glass border-b border-white/5 sticky top-0 z-50"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <button
            data-testid="reader-back-btn"
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-slate-400 hover:text-gold-400 font-outfit"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back</span>
          </button>

          {/* Clickable Surah Name */}
          <button
            data-testid="surah-name-button"
            onClick={onOpenSurahSelector}
            className="text-center flex-1 mx-4 hover:bg-white/5 rounded-lg py-1 px-2 transition-colors group"
          >
            <div className="flex items-center justify-center gap-2">
              <Book className="w-4 h-4 text-gold-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              <h1 className="font-cinzel text-lg font-semibold text-slate-100 group-hover:text-gold-400 transition-colors">
                {surah?.englishName}
              </h1>
              <ChevronDown className="w-4 h-4 text-slate-400 group-hover:text-gold-400 transition-colors" />
            </div>
            <p className="text-slate-500 text-xs font-outfit">
              {reviewMode && "Review Mode • "}
              {stackMode && `Stack ${stackLevel} • `}
              {viewMode === "flashcard"
                ? `${currentAyahIndex + 1}/${displayAyahs.length}`
                : `${totalAyahs} ayahs`}
            </p>
          </button>

          {/* Controls */}
          <div className="flex items-center gap-1">
            {onReciterChange && (
              <ReciterPicker value={reciter} onChange={onReciterChange} className="mr-1" />
            )}
            <Select value={String(selectedJuz)} onValueChange={handleJuzChange}>
              <SelectTrigger className="w-[65px] h-8 bg-islamic-blue-900/50 border-white/10 text-slate-200 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-islamic-blue-900 border-white/10 max-h-60">
                {JUZ_DATA.map((juz) => (
                  <SelectItem
                    key={juz.juz}
                    value={String(juz.juz)}
                    className="text-slate-200 text-sm"
                  >
                    Juz {juz.juz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <button
              data-testid="view-mode-surah-btn"
              onClick={() => changeViewMode("mushaf")}
              className={`w-8 h-8 rounded flex items-center justify-center ${
                viewMode === "mushaf"
                  ? "bg-gold-400 text-islamic-blue-950"
                  : "text-slate-400 hover:text-gold-400"
              }`}
              title="Surah Mode"
            >
              <Layers className="w-4 h-4" />
            </button>
            <button
              data-testid="view-mode-flashcard-btn"
              onClick={() => changeViewMode("flashcard")}
              className={`w-8 h-8 rounded flex items-center justify-center ${
                viewMode === "flashcard"
                  ? "bg-gold-400 text-islamic-blue-950"
                  : "text-slate-400 hover:text-gold-400"
              }`}
              title="Flashcard Mode"
            >
              <CreditCard className="w-4 h-4" />
            </button>
            <button
              data-testid="view-mode-page-btn"
              onClick={() => changeViewMode("page")}
              className={`w-8 h-8 rounded flex items-center justify-center ${
                viewMode === "page"
                  ? "bg-gold-400 text-islamic-blue-950"
                  : "text-slate-400 hover:text-gold-400"
              }`}
              title="Page Mode (Mushaf)"
            >
              <BookOpen className="w-4 h-4" />
            </button>
            <button
              data-testid="reader-help-btn"
              onClick={() => {
                clearTutorialFlag();
                onShowTutorial();
              }}
              className="w-8 h-8 rounded flex items-center justify-center text-slate-400 hover:text-gold-400"
              title="How to use the Reader"
            >
              <span className="text-base font-cinzel font-bold">?</span>
            </button>
            {onToggleFullscreen && (
              <button
                data-testid="fullscreen-toggle-btn"
                onClick={onToggleFullscreen}
                className="w-8 h-8 rounded flex items-center justify-center text-slate-400 hover:text-gold-400"
                title={fullscreenMode ? "Exit distraction-free mode" : "Distraction-free mode"}
                aria-label={fullscreenMode ? "Exit fullscreen reading" : "Enter fullscreen reading"}
              >
                {fullscreenMode ? (
                  <Minimize2 className="w-4 h-4" />
                ) : (
                  <Maximize2 className="w-4 h-4" />
                )}
              </button>
            )}
            <OfflineDownloadButton
              surahNumber={parseInt(surahNumber)}
              reciter={reciter}
              size="sm"
              className="ml-1"
            />
            <ThemeToggle />
          </div>
        </div>
      </div>
    </header>
  );
}
